// Utilidad para calcular promedio de un array de números
export const calculateAverage = (numbers: number[]): number => {
  if (numbers.length === 0) return 0;
  return numbers.reduce((a: number, b: number) => a + b, 0) / numbers.length;
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const fullYear = date.getFullYear();
  const monthNumber = date.getMonth() + 1; // 0-based
  const dayOfMonth = date.getDate();
  const month = String(monthNumber).padStart(2, '0');
  const day = String(dayOfMonth).padStart(2, '0');
  return `${fullYear}-${month}-${day}`;
};

/**
 * Calcula la fecha mínima desde hoy restando una cantidad de meses
 * Utilizado para limitar el rango de fechas en selectores de asistencia y ausencias
 * @param months - Cantidad de meses a restar desde hoy (por defecto 60)
 * @returns Fecha en formato YYYY-MM-DD
 */
export const getMinDateByMonths = (months: number = 60): string => {
  const minDate = new Date();
  minDate.setMonth(minDate.getMonth() - (months - 1)); // months - 1 para incluir el mes actual
  return formatDateToYYYYMMDD(minDate);
};

// Convert time value to HH:mm:ss format
export const toHHmmss = (value?: string): string => {
  if (!value) return '';
  const hhMmMatch = value.match(/^(\d{2}):(\d{2})$/);
  if (hhMmMatch) return `${value}:00`;
  const match = value.match(/^(\d{2}):(\d{2}):(\d{2})(?:\s?[APap][Mm])?$/);
  if (match) {
    const [, hh, mm, ss] = match;
    return `${hh}:${mm}:${ss}`;
  }
  const dt = new Date(value);
  if (!isNaN(dt.getTime())) {
    const hh = dt.getHours().toString().padStart(2, '0');
    const mm = dt.getMinutes().toString().padStart(2, '0');
    const ss = dt.getSeconds().toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
  return value;
};

// Capitalize only the very first character of the string (if alphabetic) and lower-case the rest
export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length === 0) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

// Capital case (Title Case style that capitalizes every word)
// Converts the first character of every word to uppercase, lowercases the rest.
// Collapses multiple spaces.
// Example: "  juAn   péRez  gOnZález" -> "Juan Pérez González"
export const toCapitalCase = (text: string): string => {
  if (!text) return '';
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Reordena un nombre completo del orden estándar de la plataforma ("Nombre
// ApellidoPaterno ApellidoMaterno") al orden "ApellidoPaterno ApellidoMaterno
// Nombres", asumiendo la convención chilena de exactamente 2 apellidos al
// final del nombre completo.
export const reorderToApellidosNombreFormat = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 3) return fullName;

  const [apellidoPaterno, apellidoMaterno] = parts.slice(-2);
  const nombres = parts.slice(0, -2).join(' ');
  return `${apellidoPaterno} ${apellidoMaterno} ${nombres}`;
};

// Comparador genérico usado por Table
export function tableValueCompare(valueA: any, valueB: any, direction: 'asc' | 'desc'): number {
  if (valueA == null) return 1;
  if (valueB == null) return -1;
  if (valueA === valueB) return 0;
  if (valueA > valueB) return direction === 'asc' ? 1 : -1;
  return direction === 'asc' ? -1 : 1; 
}

// Format date to dd/mm/yyyy
export const formatToDdMmYyyy = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  if (typeof date === 'string') {
    // Handle dd/mm/yyyy (with optional time)
    const ddmmyyyyMatch = date.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (ddmmyyyyMatch) {
      const [, d, m, y] = ddmmyyyyMatch;
      // Validate date
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
        
      if (month < 1 || month > 12) return ''; // Invalid month
      if (day < 1 || day > 31) return ''; // Simple day validation
        
      return `${d}/${m}/${y}`;
    }

    // Handle dd-mm-yyyy (with optional time)
    const ddmmyyyyDashMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (ddmmyyyyDashMatch) {
      const [, d, m, y] = ddmmyyyyDashMatch;
      // Validate
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
        
      if (month < 1 || month > 12) return '';
      if (day < 1 || day > 31) return '';

      return `${d}/${m}/${y}`;
    }
    
    // Handle yyyy-mm-dd (with optional time)
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      // No validation for ISO format to match legacy behavior/tests
      return `${d}/${m}/${y}`;
    }

    // Handle YYYYMMDD
    const compactMatch = date.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compactMatch) {
      const [, y, m, d] = compactMatch;
      // Validate
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
        
      if (month < 1 || month > 12) return '';
      if (day < 1 || day > 31) return '';

      return `${d}/${m}/${y}`;
    }
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}/${month}/${year}`;
};

export const formatToDdMmYyyyHHmm = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateValue = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateValue.getTime())) return '';

  const day = String(dateValue.getDate()).padStart(2, '0');
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const year = dateValue.getFullYear();
  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const formatToDdMmYyyyHHmmss = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateValue = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateValue.getTime())) return '';

  const day = String(dateValue.getDate()).padStart(2, '0');
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const year = dateValue.getFullYear();
  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');
  const seconds = String(dateValue.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};
